/**
 * Utility functions for hash-based routing and deep-linking to posts
 */

/**
 * Parses the URL hash into a key-value object
 * @returns {Object} Parsed hash parameters as key-value pairs
 * @example
 * // URL: #foo=bar&giggPost=abc123
 * const params = parseHashParams(); // returns { foo: 'bar', giggPost: 'abc123' }
 */
export function parseHashParams() {
  const hash = window.location.hash.substring(1);
  if (!hash) return {};

  const params = {};
  hash.split('&').forEach((param) => {
    const [key, value] = param.split('=');
    if (key && value) {
      params[key] = decodeURIComponent(value);
    }
  });

  return params;
}

/**
 * Updates the URL hash with new parameters while preserving existing ones
 * @param {string} key - Parameter key to set
 * @param {string} value - Parameter value to set
 * @example
 * // Current hash: #foo=bar
 * setHashParam('giggPost', 'abc123'); // Results in: #foo=bar&giggPost=abc123
 */
export function setHashParam(key, value) {
  const params = parseHashParams();
  params[key] = value;

  const hashString = Object.entries(params)
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join('&');

  window.history.replaceState(null, null, `#${hashString}`);
}

/**
 * Removes a parameter from the URL hash while preserving others
 * @param {string} key - Parameter key to remove
 * @example
 * // Current hash: #foo=bar&giggPost=abc123
 * removeHashParam('giggPost'); // Results in: #foo=bar
 */
export function removeHashParam(key) {
  const params = parseHashParams();
  delete params[key];

  const hashString = Object.entries(params)
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join('&');

  if (hashString) {
    window.history.replaceState(null, null, `#${hashString}`);
  } else {
    window.history.replaceState(null, null, window.location.pathname + window.location.search);
  }
}

/**
 * Gets the current giggPost parameter from the hash
 * @returns {string|null} The post ID if present, null otherwise
 * @example
 * const postId = getCurrentHashPost(); // returns 'abc123' or null
 */
export function getCurrentHashPost() {
  const params = parseHashParams();
  return params.giggPost || null;
}

/**
 * Generates a shareable URL with the giggPost hash parameter
 * @param {string} postId - The post ID to include in the URL
 * @returns {string} Complete URL with hash parameter
 * @example
 * const shareUrl = generateShareUrl('abc123');
 * // returns 'https://example.com/page#giggPost=abc123'
 */
export function generateShareUrl(postId) {
  const currentUrl = new URL(window.location.href);
  const params = parseHashParams();
  params.giggPost = postId;

  const hashString = Object.entries(params)
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join('&');

  return `${currentUrl.origin}${currentUrl.pathname}${currentUrl.search}#${hashString}`;
}

/**
 * Sets up hash change listener to handle post navigation
 * @param {Function} onPostOpen - Callback when a post should be opened
 * @param {Function} onPostClose - Callback when a post should be closed
 * @returns {Function} Cleanup function to remove the listener
 * @example
 * const cleanup = setupHashListener(openPost, closePost);
 * // Later: cleanup();
 */
export function setupHashListener(onPostOpen, onPostClose) {
  /**
   * Handles hash change events to open/close posts
   */
  const handleHashChange = () => {
    const postId = getCurrentHashPost();
    if (postId) {
      onPostOpen(postId);
    } else {
      onPostClose();
    }
  };

  window.addEventListener('hashchange', handleHashChange);
  handleHashChange();

  return () => {
    window.removeEventListener('hashchange', handleHashChange);
  };
}
